import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type {
  CandidateLimitsDto,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";

const TEXT_DEBOUNCE_MS = 400;
const MAX_CANDIDATES = 99;

const limitedSelectionCriteriaToolbar = {
  underline: false,
  headings: false,
  colors: false,
  link: false,
  table: false,
  image: false,
};

interface Props {
  eventId: string;
  current: CandidateLimitsDto | null;
  // FOA § 16-12 (3): 5 for begrenset anbudskonkurranse, 3 for the negotiated family. Null under
  // forsyningsforskriften, which sets no floor this story could verify — so the copy stops citing a
  // FOA paragraph that does not govern the procurement.
  statutoryMin: number | null;
  underFoaPartIi?: boolean;
}

// Nedvalg (§ 16-12): optional cap on how many qualified suppliers are invited to tender.
// The limits and the utvelgelseskriterier are announced in the notice and frozen at publish.
// Card styling mirrors MilestonePlanCard so the Procedure step reads as one column of cards.
export const CandidateLimitsField = ({ eventId, current, statutoryMin, underFoaPartIi = false }: Props) => {
  const { t } = useTranslation();
  const { updateField } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const enabled = current != null;
  // Forsyningsforskriften sets no floor this story could verify, so there is none to impose: the
  // stepper stops at one invited supplier and nothing is called statutory. Borrowing FOA's 5 would
  // block a shortlisting the regulation allows.
  const floor = statutoryMin ?? 1;
  // Only reachable through seeded/legacy data — the steppers below are floored at the minimum.
  const belowFloor = statutoryMin != null && current != null && current.minCandidates < statutoryMin;

  const toggle = (on: boolean) => {
    updateField({
      publicContext: {
        candidateLimits: on ? { minCandidates: floor, selectionCriteria: "" } : null,
      },
    });
  };

  // The pair must stay coherent without ever flashing an error the buyer didn't cause:
  // raising the minimum past the maximum drags the maximum along.
  const setMinimum = (value: number) => {
    if (Number.isNaN(value) || current == null) return;
    updateField({
      publicContext: {
        candidateLimits: {
          minCandidates: value,
          ...(current.maxCandidates != null && current.maxCandidates < value && { maxCandidates: value }),
        },
      },
    });
  };

  const setMaximum = (value: number) => {
    if (Number.isNaN(value)) return;
    updateField({ publicContext: { candidateLimits: { maxCandidates: value } } });
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
      <FormControl display="flex" alignItems="flex-start" justifyContent="space-between" gap="6">
        <Box>
          <FormLabel htmlFor="candidateLimitsEnabled" mb="1" cursor="pointer">
            <Heading as="h3" size="sm">
              {t("Limit how many suppliers are invited to tender")}
            </Heading>
          </FormLabel>
          <Text fontSize="sm" color="smMuted">
            {enabled
              ? t("The limits and selection criteria are announced in the notice and cannot change after publication.")
              : statutoryMin == null
                ? t("Without a limit, every qualified supplier is invited.")
                : underFoaPartIi
                  ? t("Without a limit, every qualified supplier is invited (FOA § 8-9).")
                  : t("Without a limit, every qualified supplier is invited (FOA § 16-12).")}
          </Text>
        </Box>
        <Switch
          id="candidateLimitsEnabled"
          isChecked={enabled}
          onChange={(e) => toggle(e.target.checked)}
          colorScheme="teal"
          mt="1"
        />
      </FormControl>

      {current != null && (
        <Box mt="5">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="6">
            <FormControl isInvalid={belowFloor}>
              <FormLabel fontSize="sm">{t("Minimum number invited")}</FormLabel>
              <NumberInput
                value={current.minCandidates}
                min={floor}
                max={MAX_CANDIDATES}
                onChange={(_, value) => setMinimum(value)}
                width="28"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              {belowFloor ? (
                <Text mt="1" fontSize="sm" color="smError">
                  {underFoaPartIi
                    ? t("zz_FieldErrorCode_CandidateMinimumBelowPartIiFloor", { min: statutoryMin })
                    : t("zz_FieldErrorCode_CandidateMinimumBelowFloor", { min: statutoryMin })}
                </Text>
              ) : (
                statutoryMin != null && (
                  <FormHelperText>{t("The statutory minimum is {{min}}.", { min: statutoryMin })}</FormHelperText>
                )
              )}
            </FormControl>

            <FormControl>
              <Flex align="center" justify="space-between" mb="2" maxW="64">
                <FormLabel fontSize="sm" mb="0" htmlFor="candidateLimitsHasMax" cursor="pointer">
                  {t("Set an upper limit")}
                </FormLabel>
                <Checkbox
                  id="candidateLimitsHasMax"
                  isChecked={current.maxCandidates != null}
                  onChange={(e) =>
                    updateField({
                      publicContext: {
                        candidateLimits: {
                          maxCandidates: e.target.checked ? Math.max(current.minCandidates, floor) : null,
                        },
                      },
                    })
                  }
                  colorScheme="teal"
                />
              </Flex>
              {current.maxCandidates != null && (
                <NumberInput
                  value={current.maxCandidates}
                  min={current.minCandidates}
                  max={MAX_CANDIDATES}
                  onChange={(_, value) => setMaximum(value)}
                  width="28"
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              )}
            </FormControl>
          </SimpleGrid>

          <FormControl mt="5">
            <FormLabel fontSize="sm">{t("Selection criteria for choosing among the qualified")}</FormLabel>
            <TipTap
              content={current.selectionCriteria ?? ""}
              onUpdate={(value) =>
                updateField(
                  { publicContext: { candidateLimits: { selectionCriteria: value } } },
                  { debounceMs: TEXT_DEBOUNCE_MS },
                )
              }
              toolbar={limitedSelectionCriteriaToolbar}
              debounceWait={0}
            />
            <FormHelperText>
              {statutoryMin == null
                ? t("Objective and non-discriminatory criteria, announced in the notice.")
                : underFoaPartIi
                  ? t("Objective and non-discriminatory criteria, announced in the notice (FOA § 8-9).")
                  : t("Objective and non-discriminatory criteria, announced in the notice (FOA § 16-12).")}
            </FormHelperText>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};
